"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { API_BASE_URL } from "@/lib/api"

interface Session {
  id: string
  name: string
  drawTime: string
  active: boolean
  stats: {
    singleBets: number
    rajshreeBets: number
    results: number
  }
}

export function DeclareResults() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [selectedSession, setSelectedSession] = useState("")
  const [lotteryType, setLotteryType] = useState("single")
  const [results, setResults] = useState<{ category: string; number: number }[]>([])
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const categories = ["1st", "2nd", "3rd", "4th", "5th"]

  const addResult = () => {
    setResults([...results, { category: "1st", number: 0 }])
  }

  const updateResult = (index: number, field: string, value: string | number) => {
    const newResults = [...results]
    newResults[index] = { ...newResults[index], [field]: value }
    setResults(newResults)
  }

  const removeResult = (index: number) => {
    setResults(results.filter((_, i) => i !== index))
  }

  const declareResults = async () => {
    if (!selectedSession) {
      toast({
        title: "Error",
        description: "Please select a session",
        variant: "destructive",
      })
      return
    }

    if (results.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one result",
        variant: "destructive",
      })
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const endpoint = lotteryType === 'single' ? 'single-results' : 'rajshree-results'
      
      const response = await fetch(`${API_BASE_URL}/admin/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: selectedSession,
          results: results
        }),
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: data.message,
          variant: "default",
        })
        setResults([])
        setSelectedSession("")
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to declare results",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error declaring results:', error)
      toast({
        title: "Error",
        description: "Failed to declare results",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_BASE_URL}/sessions/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSessions(data.sessions || [])
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    }
  }

  // Fetch sessions on component mount
  useState(() => {
    fetchSessions()
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Declare Lottery Results</CardTitle>
        <CardDescription>
          Declare winning numbers for single and rajshree lotteries
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Lottery Type</Label>
            <Select value={lotteryType} onValueChange={setLotteryType}>
              <SelectTrigger>
                <SelectValue placeholder="Select lottery type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single Lottery</SelectItem>
                <SelectItem value="rajshree">Rajshree Lottery</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Session</Label>
            <Select value={selectedSession} onValueChange={setSelectedSession}>
              <SelectTrigger>
                <SelectValue placeholder="Select session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((session) => (
                  <SelectItem key={session.id} value={session.id}>
                    {session.name} ({session.drawTime}) - 
                    Singles: {session.stats.singleBets}, 
                    Rajshree: {session.stats.rajshreeBets}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label>Winning Numbers</Label>
            <Button type="button" variant="outline" size="sm" onClick={addResult}>
              Add Result
            </Button>
          </div>

          {results.map((result, index) => (
            <div key={index} className="flex gap-2 items-center">
              <Select 
                value={result.category} 
                onValueChange={(value) => updateResult(index, 'category', value)}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Input
                type="number"
                min="0"
                max="9"
                value={result.number}
                onChange={(e) => updateResult(index, 'number', parseInt(e.target.value) || 0)}
                placeholder="Number (0-9)"
                className="w-32"
              />
              
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeResult(index)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>

        <Button 
          onClick={declareResults}
          disabled={loading || !selectedSession || results.length === 0}
          className="w-full bg-green-600 hover:bg-green-700"
        >
          {loading ? "Declaring Results..." : "Declare Results"}
        </Button>
      </CardContent>
    </Card>
  )
}